/**
 * Subtitles as timed, structured cues (not text blobs). Times are integer ticks. SRT, WebVTT and (1.9.0) TTML are
 * import/export formats only (subtitle-formats.ts).
 *
 * 1.9.0:
 *  - styles: `CueSchema.style` (a cue's own), `TrackStyleSchema` (a track's default); placement is one of 9 cells.
 *    Inline italic, bold and underline stay in `text` as <i>, <b>, <u> (caption.ts cueRuns reads them). Additive: a
 *    1.8.3 cue or track without styles parses and re-saves byte for byte.
 *  - overlaps: two cues in the same placement row (top, middle, bottom) may not overlap; cues in different rows may
 *    (TTML often shows a top and a bottom cue at once).
 *  - the cue edits shared by the asset Subtitles tab, the subtitle strip and the sequence ops (`newCueAt`,
 *    `setCueEdge`, `moveCue`, `nudgeCue`, `splitCue`, `mergeCues`, `applyCueStyle`): frame-snapped, at least a frame
 *    long, and kept MIN_CUE_GAP_FRAMES from their same-row neighbours unless they are closer already.
 *  - `cueMetrics` for the live counters; the sidecar formats and their file extensions.
 */
import { z } from 'zod';
import { TICKS_PER_SECOND, ticksPerFrame, type Rational } from './time.ts';
import {
  CUE_ALIGNS, CUE_STYLE_LIMITS, cueRow, effectiveCueStyle, stripLegacyAlign,
  type CueRow, type CueStyle,
} from './caption.ts';

/* ---------------------------------------------------------------- styles */

const StyleFields = {
  align: z.enum(CUE_ALIGNS).optional(),
  color: z.string().regex(/^#[0-9a-f]{6}$/, 'A colour is #rrggbb in lowercase').optional(),
  background: z.number().min(CUE_STYLE_LIMITS.backgroundMin).max(CUE_STYLE_LIMITS.backgroundMax).optional(),
  size: z.number().min(CUE_STYLE_LIMITS.sizeMin).max(CUE_STYLE_LIMITS.sizeMax).optional(),
};

/** A cue's own style; every field optional, a field left out comes from the track style. */
export const CueStyleSchema = z.object(StyleFields);
/** A track's default style (subtitle_tracks.style, sequence subtitles.style); `{}` is the default look. */
export const TrackStyleSchema = z.object(StyleFields);
export type TrackStyle = z.infer<typeof TrackStyleSchema>;
/** A style with every field set (what the burn-in job carries per cue). */
export const ResolvedCueStyleSchema = z.object({
  align: z.enum(CUE_ALIGNS),
  color: z.string().regex(/^#[0-9a-f]{6}$/),
  background: z.number().min(CUE_STYLE_LIMITS.backgroundMin).max(CUE_STYLE_LIMITS.backgroundMax),
  size: z.number().min(CUE_STYLE_LIMITS.sizeMin).max(CUE_STYLE_LIMITS.sizeMax),
});

export const CueSchema = z.object({
  id: z.string().min(1).max(64),
  startTicks: z.number().int().nonnegative(),
  endTicks: z.number().int().nonnegative(),
  text: z.string().max(2000),
  speaker: z.string().max(120).optional(),
  /** 1.9.0: the cue's own style; left out: the track's. */
  style: CueStyleSchema.optional(),
});
export type Cue = z.infer<typeof CueSchema>;

/** The length of a new cue (1.9.0; 2 s before). */
export const NEW_CUE_SECONDS = 3;
/** The gap kept between cues of one row (the QC rule's 2 frames). */
export const MIN_CUE_GAP_FRAMES = 2;

/* ---------------------------------------------------------------- rules and checks */

export interface SubtitleRules {
  maxCharsPerLine: number;
  maxLines: number;
  maxCharsPerSecond: number;
  minDurationTicks: number;
  maxDurationTicks: number;
}

export const DEFAULT_SUBTITLE_RULES: SubtitleRules = {
  maxCharsPerLine: 42,
  maxLines: 2,
  maxCharsPerSecond: 17,
  minDurationTicks: Math.round(TICKS_PER_SECOND * 0.833),
  maxDurationTicks: TICKS_PER_SECOND * 7,
};

export type CueIssueKind = 'invalid_range' | 'overlap' | 'too_short' | 'too_long' | 'reading_speed' | 'line_length' | 'too_many_lines' | 'empty';

export interface CueIssue {
  cueId: string;
  kind: CueIssueKind;
  severity: 'error' | 'warning';
  message: string;
}

/** Visible characters, ignoring simple markup such as <i>. */
export function visibleText(text: string): string {
  return text.replace(/<\/?[a-z][^>]*>/gi, '').replace(/\{\\[^}]*\}/g, '');
}

/** The placement row a cue is drawn in, with the track's style. */
export const rowOfCue = (cue: Pick<Cue, 'text' | 'style'>, trackStyle?: CueStyle | null): CueRow => cueRow(effectiveCueStyle(trackStyle, cue).align);

/** The live counters for one cue (1.9.0). */
export interface CueMetrics {
  /** Characters per second (visible characters, a newline counts as a space); 0 for a cue without length. */
  cps: number;
  /** The longest line's visible characters. */
  longestLine: number;
  lines: number;
  durationTicks: number;
  /** This cue's issues: its own, and with `cues` given, overlaps with its same-row neighbours. */
  issues: CueIssue[];
}

function ownIssues(c: Cue, rules: SubtitleRules, durationTicks: number | undefined, overlapsNext: boolean, overlapsPrevious = false): { issues: CueIssue[]; cps: number; longestLine: number; lines: number } {
  const issues: CueIssue[] = [];
  const len = c.endTicks - c.startTicks;
  const text = visibleText(c.text).trim();
  if (len <= 0) issues.push({ cueId: c.id, kind: 'invalid_range', severity: 'error', message: 'Ends before it starts' });
  if (durationTicks !== undefined && c.endTicks > durationTicks) {
    issues.push({ cueId: c.id, kind: 'invalid_range', severity: 'error', message: 'Runs past the end of the media' });
  }
  if (!text) issues.push({ cueId: c.id, kind: 'empty', severity: 'warning', message: 'No text' });
  if (overlapsNext) issues.push({ cueId: c.id, kind: 'overlap', severity: 'error', message: 'Overlaps the next cue' });
  if (overlapsPrevious) issues.push({ cueId: c.id, kind: 'overlap', severity: 'error', message: 'Overlaps the previous cue' });
  if (len > 0 && len < rules.minDurationTicks) {
    issues.push({ cueId: c.id, kind: 'too_short', severity: 'warning', message: `Shorter than ${(rules.minDurationTicks / TICKS_PER_SECOND).toFixed(2)} s` });
  }
  if (len > rules.maxDurationTicks) {
    issues.push({ cueId: c.id, kind: 'too_long', severity: 'warning', message: `Longer than ${rules.maxDurationTicks / TICKS_PER_SECOND} s` });
  }
  const lines = text.split('\n');
  const longestLine = Math.max(0, ...lines.map((l) => [...l].length));
  if (lines.length > rules.maxLines) issues.push({ cueId: c.id, kind: 'too_many_lines', severity: 'warning', message: `More than ${rules.maxLines} lines` });
  if (longestLine > rules.maxCharsPerLine) {
    issues.push({ cueId: c.id, kind: 'line_length', severity: 'warning', message: `A line exceeds ${rules.maxCharsPerLine} characters` });
  }
  let cps = 0;
  if (len > 0) {
    cps = [...text.replace(/\n/g, ' ')].length / (len / TICKS_PER_SECOND);
    if (cps > rules.maxCharsPerSecond) {
      issues.push({ cueId: c.id, kind: 'reading_speed', severity: 'warning', message: `${cps.toFixed(1)} characters per second (limit ${rules.maxCharsPerSecond})` });
    }
  }
  return { issues, cps, longestLine, lines: text ? lines.length : 0 };
}

/** The ids of the cues that overlap the next cue of their own row (by start). */
function overlapping(cues: readonly Cue[], trackStyle: CueStyle | null | undefined): { next: Set<string>; previous: Set<string> } {
  const next = new Set<string>(), previous = new Set<string>();
  const rows = new Map<CueRow, Cue[]>();
  for (const c of cues) {
    const r = rowOfCue(c, trackStyle);
    const list = rows.get(r);
    if (list) list.push(c); else rows.set(r, [c]);
  }
  for (const list of rows.values()) {
    list.sort((a, b) => a.startTicks - b.startTicks);
    for (let i = 0; i + 1 < list.length; i++) {
      if (list[i + 1]!.startTicks < list[i]!.endTicks) { next.add(list[i]!.id); previous.add(list[i + 1]!.id); }
    }
  }
  return { next, previous };
}

/**
 * Every cue's issues, by start. An overlap is an error only between cues of the same row (1.9.0: cues in different
 * rows may be shown at once); `trackStyle` gives the rows of cues without their own cell.
 */
export function validateCues(cues: Cue[], rules: SubtitleRules = DEFAULT_SUBTITLE_RULES, durationTicks?: number, trackStyle?: CueStyle | null): CueIssue[] {
  const sorted = [...cues].sort((a, b) => a.startTicks - b.startTicks);
  const { next } = overlapping(sorted, trackStyle);
  return sorted.flatMap((c) => ownIssues(c, rules, durationTicks, next.has(c.id)).issues);
}

/**
 * The live counters for a cue: reading speed, the longest line, lines, length and its issues. With `cues` (the whole
 * track, this cue included or not) the issues include overlaps with its same-row neighbours, both ways.
 */
export function cueMetrics(cue: Cue, rules: SubtitleRules = DEFAULT_SUBTITLE_RULES, opts: { cues?: readonly Cue[]; trackStyle?: CueStyle | null; durationTicks?: number } = {}): CueMetrics {
  let overlapsNext = false, overlapsPrevious = false;
  if (opts.cues) {
    const row = rowOfCue(cue, opts.trackStyle);
    for (const o of opts.cues) {
      if (o.id === cue.id || rowOfCue(o, opts.trackStyle) !== row) continue;
      if (o.startTicks < cue.endTicks && o.endTicks > cue.startTicks) {
        if (o.startTicks >= cue.startTicks) overlapsNext = true; else overlapsPrevious = true;
      }
    }
  }
  const r = ownIssues(cue, rules, opts.durationTicks, overlapsNext, overlapsPrevious);
  return { cps: r.cps, longestLine: r.longestLine, lines: r.lines, durationTicks: Math.max(0, cue.endTicks - cue.startTicks), issues: r.issues };
}

/* ---------------------------------------------------------------- shared cue edits */

/** What the edits need to know: the frame (a rate, or ticks per frame), the media's end and the track's style. */
export interface CueEditContext {
  /** Default 25 fps. */
  frameRate?: Rational;
  /** Ticks per frame; overrides frameRate. */
  frameTicks?: number;
  /** The media's (or sequence's) end; cues stay before it. Unbounded when left out. */
  durationTicks?: number | null;
  /** The track's style, for the rows of cues without their own cell. */
  trackStyle?: CueStyle | null;
  /** The gap kept from same-row neighbours, in frames (default MIN_CUE_GAP_FRAMES). */
  gapFrames?: number;
}

const FALLBACK_RATE: Rational = { num: 25, den: 1 };
export const cueFrameTicks = (ctx: CueEditContext): number => Math.max(1, ctx.frameTicks ?? ticksPerFrame(ctx.frameRate ?? FALLBACK_RATE));
/** The nearest frame boundary. */
export const snapToFrame = (ticks: number, frameTicks: number): number => Math.max(0, Math.round(Math.round(ticks / frameTicks) * frameTicks));

let cueSeq = 0;
/** A new cue id (unique within a session). */
export function newCueId(): string {
  cueSeq = (cueSeq + 1) % 1e6;
  return `c${Date.now().toString(36)}${cueSeq.toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;
}

interface Bounds { floor: number; ceil: number; tpf: number; gap: number }

/**
 * How far a cue's start can go back (`floor`) and its end forward (`ceil`): to its same-row neighbours minus the gap
 * (only neighbours it does not overlap; a cue closer than the gap already is not pushed back), to 0 and to the end.
 */
function bounds(cues: readonly Cue[], c: Cue, ctx: CueEditContext): Bounds {
  const tpf = cueFrameTicks(ctx);
  const gap = Math.max(0, ctx.gapFrames ?? MIN_CUE_GAP_FRAMES) * tpf;
  const row = rowOfCue(c, ctx.trackStyle);
  let floor = 0, ceil = ctx.durationTicks ?? Number.MAX_SAFE_INTEGER;
  for (const o of cues) {
    if (o === c || o.id === c.id || rowOfCue(o, ctx.trackStyle) !== row) continue;
    if (o.endTicks <= c.startTicks) floor = Math.max(floor, o.endTicks + gap);
    else if (o.startTicks >= c.endTicks) ceil = Math.min(ceil, o.startTicks - gap);
  }
  return { floor: Math.min(floor, c.startTicks), ceil: Math.max(ceil, c.endTicks), tpf, gap };
}

const replace = (cues: readonly Cue[], next: Cue): Cue[] => cues.map((c) => (c.id === next.id ? next : c));

/**
 * Set a cue's start or end to `ticks`, snapped to a frame: the start stays at its floor or later, the end at its
 * ceiling or earlier, and the cue at least a frame long. The same array when nothing changes or the cue is unknown.
 */
export function setCueEdge(cues: Cue[], id: string, edge: 'start' | 'end', ticks: number, ctx: CueEditContext = {}): Cue[] {
  return setEdge(cues, id, edge, ticks, ctx, true);
}

function setEdge(cues: Cue[], id: string, edge: 'start' | 'end', ticks: number, ctx: CueEditContext, snap: boolean): Cue[] {
  const c = cues.find((x) => x.id === id);
  if (!c) return cues;
  const b = bounds(cues, c, ctx);
  const t = snap ? snapToFrame(ticks, b.tpf) : Math.max(0, Math.round(ticks));
  let { startTicks, endTicks } = c;
  if (edge === 'start') startTicks = Math.max(b.floor, Math.min(t, c.endTicks - b.tpf));
  else endTicks = Math.min(b.ceil, Math.max(t, c.startTicks + b.tpf));
  startTicks = Math.max(0, Math.round(startTicks));
  endTicks = Math.max(0, Math.round(endTicks));
  if (startTicks === c.startTicks && endTicks === c.endTicks) return cues;
  return replace(cues, { ...c, startTicks, endTicks });
}

/** Move a whole cue to start at `toStartTicks` (snapped), keeping its length, between its neighbours. */
export function moveCue(cues: Cue[], id: string, toStartTicks: number, ctx: CueEditContext = {}): Cue[] {
  return move(cues, id, toStartTicks, ctx, true);
}

function move(cues: Cue[], id: string, toStartTicks: number, ctx: CueEditContext, snap: boolean): Cue[] {
  const c = cues.find((x) => x.id === id);
  if (!c) return cues;
  const b = bounds(cues, c, ctx);
  const len = c.endTicks - c.startTicks;
  const t = snap ? snapToFrame(toStartTicks, b.tpf) : Math.max(0, Math.round(toStartTicks));
  const startTicks = Math.max(b.floor, Math.min(t, b.ceil - len));
  if (startTicks === c.startTicks) return cues;
  return replace(cues, { ...c, startTicks, endTicks: startTicks + len });
}

/**
 * Move a cue's start, end or both by whole frames (the nudge keys, ⇧ ten frames; the timeline's cue trims): steps of
 * a frame from where the edge is, so an edge off the frame grid (an imported file) keeps its offset.
 */
export function nudgeCue(cues: Cue[], id: string, edge: 'start' | 'end' | 'both', frames: number, ctx: CueEditContext = {}): Cue[] {
  const c = cues.find((x) => x.id === id);
  if (!c || !frames) return cues;
  const d = Math.round(frames) * cueFrameTicks(ctx);
  if (edge === 'both') return move(cues, id, c.startTicks + d, ctx, false);
  return setEdge(cues, id, edge, (edge === 'start' ? c.startTicks : c.endTicks) + d, ctx, false);
}

const byStart = (a: Cue, b: Cue) => a.startTicks - b.startTicks;

/**
 * A new cue at the playhead (snapped), NEW_CUE_SECONDS long, in the row its style (or the track's) gives: it starts
 * after a same-row cue the playhead is in (plus the gap), and ends before the next one (minus the gap) and at the
 * media's end. Null when there is less than a frame of room. The cue is inserted in start order.
 */
export function newCueAt(cues: Cue[], atTicks: number, ctx: CueEditContext = {}, init: { id?: string; text?: string; speaker?: string; style?: CueStyle } = {}): { cues: Cue[]; cue: Cue } | null {
  const tpf = cueFrameTicks(ctx);
  const gap = Math.max(0, ctx.gapFrames ?? MIN_CUE_GAP_FRAMES) * tpf;
  const text = init.text ?? '';
  const row = rowOfCue({ text, style: init.style }, ctx.trackStyle);
  const same = cues.filter((c) => rowOfCue(c, ctx.trackStyle) === row).sort(byStart);
  let start = snapToFrame(Math.max(0, atTicks), tpf);
  // Out of any same-row cue it lands in (or within the gap after one); repeated for cues that overlap each other.
  for (let moved = true; moved;) {
    moved = false;
    for (const c of same) {
      if (c.startTicks <= start && start < c.endTicks + gap && c.endTicks > c.startTicks) {
        const next = Math.round(Math.ceil((c.endTicks + gap) / tpf - 1e-9) * tpf);
        if (next > start) { start = next; moved = true; }
      }
    }
  }
  let end = start + snapToFrame(NEW_CUE_SECONDS * TICKS_PER_SECOND, tpf);
  const following = same.find((c) => c.startTicks >= start);
  if (following) end = Math.min(end, following.startTicks - gap);
  if (ctx.durationTicks != null) end = Math.min(end, ctx.durationTicks);
  if (end - start < tpf) return null;
  const cue: Cue = { id: init.id ?? newCueId(), startTicks: start, endTicks: end, text, ...(init.speaker ? { speaker: init.speaker } : {}), ...(init.style ? { style: init.style } : {}) };
  const at = cues.findIndex((c) => c.startTicks > start);
  const out = [...cues];
  out.splice(at < 0 ? out.length : at, 0, cue);
  return { cues: out, cue };
}

const INLINE = /<(\/?)([ibu])(?:[.\s][^>]*)?>/gi;

/** The <i>, <b>, <u> open at `index` of the text, outermost first. */
function openTagsAt(text: string, index: number): string[] {
  const open: string[] = [];
  for (const m of text.slice(0, index).matchAll(INLINE)) {
    const name = m[2]!.toLowerCase();
    if (m[1]) { const k = open.lastIndexOf(name); if (k >= 0) open.splice(k, 1); } else open.push(name);
  }
  return open;
}

/** Where to split text with no caret: the whitespace (outside tags) nearest the playhead's share of the text. */
function splitPoint(text: string, share: number): number {
  const target = Math.round(Math.min(1, Math.max(0, share)) * text.length);
  const spaces: number[] = [];
  let inTag = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '<') inTag = true;
    else if (ch === '>') inTag = false;
    else if (!inTag && /\s/.test(ch)) spaces.push(i);
  }
  if (!spaces.length) return target;
  return spaces.reduce((best, i) => (Math.abs(i - target) < Math.abs(best - target) ? i : best), spaces[0]!);
}

/**
 * Split a cue at the playhead (snapped): the first part ends the gap before it, the second starts at it. The text is
 * split at `caret` (an index into the text) or, without one, at the whitespace nearest the playhead's share of the
 * text; <i>, <b>, <u> open at the split are closed in the first part and opened again in the second, and an old
 * leading `{\anN}` is repeated. Null when either part would be shorter than a frame.
 */
export function splitCue(cues: Cue[], id: string, atTicks: number, ctx: CueEditContext = {}, opts: { caret?: number | null; newId?: string } = {}): { cues: Cue[]; first: Cue; second: Cue } | null {
  const c = cues.find((x) => x.id === id);
  if (!c) return null;
  const tpf = cueFrameTicks(ctx);
  const gap = Math.max(0, ctx.gapFrames ?? MIN_CUE_GAP_FRAMES) * tpf;
  const at = snapToFrame(atTicks, tpf);
  const firstEnd = at - gap;
  if (firstEnd - c.startTicks < tpf || c.endTicks - at < tpf) return null;
  const k = opts.caret != null && opts.caret > 0 && opts.caret < c.text.length
    ? opts.caret
    : splitPoint(c.text, (at - c.startTicks) / (c.endTicks - c.startTicks));
  const open = openTagsAt(c.text, k);
  const legacy = /^\s*\{\\an[1-9]\}/.exec(c.text)?.[0];
  const a = c.text.slice(0, k).replace(/\s+$/, '') + [...open].reverse().map((t) => `</${t}>`).join('');
  const b = (legacy && !c.style?.align ? legacy.trim() : '') + open.map((t) => `<${t}>`).join('') + c.text.slice(k).replace(/^\s+/, '');
  const first: Cue = { ...c, endTicks: firstEnd, text: a };
  const second: Cue = { ...c, id: opts.newId ?? newCueId(), startTicks: at, text: b };
  const out: Cue[] = [];
  for (const x of cues) {
    if (x.id === id) out.push(first, second); else out.push(x);
  }
  return { cues: out, first, second };
}

/**
 * Merge a cue with the next cue of its row (by start): from the first's start to the later end, the texts on two
 * lines (the second's old `{\anN}` dropped), the first's id, style and speaker (else the second's speaker). Null
 * when there is no next cue in the row.
 */
export function mergeCues(cues: Cue[], id: string, ctx: CueEditContext = {}): { cues: Cue[]; cue: Cue } | null {
  const c = cues.find((x) => x.id === id);
  if (!c) return null;
  const row = rowOfCue(c, ctx.trackStyle);
  const same = cues.filter((x) => rowOfCue(x, ctx.trackStyle) === row).sort((a, b) => byStart(a, b) || cues.indexOf(a) - cues.indexOf(b));
  const n = same[same.indexOf(c) + 1];
  if (!n) return null;
  const speaker = c.speaker ?? n.speaker;
  const cue: Cue = { ...c, endTicks: Math.max(c.endTicks, n.endTicks), text: `${c.text}\n${stripLegacyAlign(n.text)}`, ...(speaker ? { speaker } : {}) };
  return { cues: cues.filter((x) => x !== n).map((x) => (x === c ? cue : x)), cue };
}

/** A style change: a value sets a field, null removes it (back to the track's); `null` for the whole patch removes the cue's style. */
export type CueStylePatch = { [K in keyof CueStyle]?: CueStyle[K] | null };

/**
 * Apply a style change to the cues `ids`. Setting or clearing the cell also drops an old leading `{\anN}` from the
 * text, so the grid is the one place the cell is set. A style left empty is removed. The same array when nothing
 * changes or the result is not a valid style.
 */
export function applyCueStyle(cues: Cue[], ids: readonly string[], patch: CueStylePatch | null): Cue[] {
  const want = new Set(ids);
  let changed = false;
  const out = cues.map((c) => {
    if (!want.has(c.id)) return c;
    let style: Record<string, unknown> = {};
    if (patch) {
      style = { ...(c.style ?? {}) };
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined) delete style[k]; else style[k] = typeof v === 'string' ? v.toLowerCase() : v;
      }
    }
    const parsed = CueStyleSchema.safeParse(style);
    if (!parsed.success) return c;
    const touchesAlign = !patch || 'align' in patch;
    const text = touchesAlign ? stripLegacyAlign(c.text) : c.text;
    const next: Cue = { ...c, text };
    if (Object.keys(parsed.data).length) next.style = parsed.data; else delete next.style;
    if (JSON.stringify(next) === JSON.stringify(c)) return c;
    changed = true;
    return next;
  });
  return changed ? out : cues;
}

/** A track style with every field valid, or null for none (empty). */
export function tidyTrackStyle(style: TrackStyle | null | undefined): TrackStyle | null {
  if (!style) return null;
  const parsed = TrackStyleSchema.safeParse(style);
  if (!parsed.success || !Object.keys(parsed.data).length) return null;
  return parsed.data;
}

/* ---------------------------------------------------------------- sidecar and download formats */

/**
 * The subtitle file formats PRISM writes (1.9.0): as export sidecars, the Subtitles tab's download, the export route's
 * `format`. `ttml` is TTML IMSC 1.1 Text, `ebu-tt-d` EBU-TT-D.
 */
export const SIDECAR_FORMATS = ['srt', 'vtt', 'ttml', 'ebu-tt-d'] as const;
export type SidecarFormat = (typeof SIDECAR_FORMATS)[number];
/** What an export delivers when it states no sidecar formats (exports.sidecar_formats NULL), as before 1.9.0. */
export const DEFAULT_SIDECAR_FORMATS: readonly SidecarFormat[] = Object.freeze(['srt', 'vtt'] as SidecarFormat[]);
/** The file extension per format: <name>.<extension>. */
export const SIDECAR_EXTENSIONS: Readonly<Record<SidecarFormat, string>> = Object.freeze({ srt: 'srt', vtt: 'vtt', ttml: 'imsc.ttml', 'ebu-tt-d': 'ebu-tt-d.ttml' });
/** The content type per format (without charset). */
export const SIDECAR_CONTENT_TYPES: Readonly<Record<SidecarFormat, string>> = Object.freeze({ srt: 'application/x-subrip', vtt: 'text/vtt', ttml: 'application/ttml+xml', 'ebu-tt-d': 'application/ttml+xml' });
export const SIDECAR_LABELS: Readonly<Record<SidecarFormat, string>> = Object.freeze({ srt: 'SRT', vtt: 'WebVTT', ttml: 'TTML (IMSC 1.1)', 'ebu-tt-d': 'EBU-TT-D' });
/** Whether a format carries the cue background and size (TTML only); SRT and WebVTT carry the cell and the colour. */
export const SIDECAR_KEEPS_BACKGROUND_AND_SIZE: Readonly<Record<SidecarFormat, boolean>> = Object.freeze({ srt: false, vtt: false, ttml: true, 'ebu-tt-d': true });
export const isSidecarFormat = (v: unknown): v is SidecarFormat => typeof v === 'string' && (SIDECAR_FORMATS as readonly string[]).includes(v);
/** The request field `sidecarFormats`: 1–4 formats, each once; left out: DEFAULT_SIDECAR_FORMATS. */
export const SidecarFormatsSchema = z.array(z.enum(SIDECAR_FORMATS)).min(1).max(SIDECAR_FORMATS.length)
  .refine((xs) => new Set(xs).size === xs.length, 'Each sidecar format once');

/** A subtitle file's name: `<label>.<language>.<extension>`, the label kept to letters, digits, space, _ and -. */
export const subtitleFileName = (label: string, language: string, format: SidecarFormat): string =>
  `${label.replace(/[^\p{L}\p{N} _-]/gu, '_')}.${language}.${SIDECAR_EXTENSIONS[format]}`;

/* ---------------------------------------------------------------- other helpers */

/** Shift every cue by an offset (negative moves earlier); cues before zero are clamped. */
export function shiftCues(cues: Cue[], offsetTicks: number): Cue[] {
  return cues.map((c) => ({ ...c, startTicks: Math.max(0, c.startTicks + offsetTicks), endTicks: Math.max(0, c.endTicks + offsetTicks) }));
}

/** Right-to-left detection for display direction. */
export function isRtl(text: string): boolean {
  return /[֐-ࣿיִ-﷿ﹰ-﻿]/.test(visibleText(text));
}
